import { useState, useRef, useDeferredValue, useCallback, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";

const ITEM_HEIGHT = 32;
const LIST_HEIGHT = 280;

interface VirtualizedComboboxProps {
  options: string[];
  selected: string;
  searchPlaceholder: string;
  emptyMessage: string;
  onSelect: (value: string) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

export function VirtualizedCombobox({
  options,
  selected,
  searchPlaceholder,
  emptyMessage,
  onSelect,
  searchValue,
  onSearchChange,
}: VirtualizedComboboxProps) {
  const isControlledSearch = searchValue !== undefined && onSearchChange !== undefined;
  const [internalSearch, setInternalSearch] = useState("");
  const rawSearch = isControlledSearch ? searchValue : internalSearch;
  const deferredSearch = useDeferredValue(rawSearch);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (isControlledSearch) {
        onSearchChange(val);
      } else {
        setInternalSearch(val);
      }
    },
    [isControlledSearch, onSearchChange],
  );

  const filtered = deferredSearch
    ? options.filter((o) => o.toLowerCase().includes(deferredSearch.toLowerCase()))
    : options;

  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 5,
  });

  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  const focusItemAt = useCallback(
    (index: number) => {
      if (index < 0 || index >= filtered.length) return;
      virtualizer.scrollToIndex(index, { align: "auto" });
      setFocusIndex(index);
    },
    [filtered.length, virtualizer],
  );

  // Focus the item once it's rendered after scrollToIndex
  useEffect(() => {
    if (focusIndex === null) return;
    const frame = requestAnimationFrame(() => {
      const el = scrollRef.current?.querySelector<HTMLElement>(
        `[data-virt-index="${focusIndex}"]`,
      );
      el?.focus();
      setFocusIndex(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [focusIndex]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        focusItemAt(0);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        focusItemAt(Math.max(filtered.length - 1, 0));
      }
    },
    [filtered.length, focusItemAt],
  );

  const handleItemKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, value: string, visibleIndex: number) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(value);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        focusItemAt(visibleIndex + 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (visibleIndex === 0) {
          scrollRef.current
            ?.closest("[data-combobox-root]")
            ?.querySelector<HTMLInputElement>("input")
            ?.focus();
        } else {
          focusItemAt(visibleIndex - 1);
        }
      }
    },
    [onSelect, focusItemAt],
  );

  return (
    <div data-combobox-root className="flex flex-col">
      <div className="flex items-center border-b px-3">
        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
        <input
          autoFocus
          value={rawSearch}
          onChange={handleSearchChange}
          onKeyDown={handleInputKeyDown}
          placeholder={searchPlaceholder}
          className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <div
          ref={scrollRef}
          style={{ height: Math.min(filtered.length * ITEM_HEIGHT + 8, LIST_HEIGHT) }}
          className="overflow-y-auto overscroll-contain p-1"
        >
          <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
            {virtualizer.getVirtualItems().map((vItem) => {
              const keyword = filtered[vItem.index];
              const isSelected = selected === keyword;
              return (
                <div
                  key={keyword}
                  data-virt-index={vItem.index}
                  tabIndex={0}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => onSelect(keyword)}
                  onKeyDown={(e) => handleItemKeyDown(e, keyword, vItem.index)}
                  style={{
                    position: "absolute",
                    top: vItem.start,
                    left: 0,
                    right: 0,
                    height: ITEM_HEIGHT,
                  }}
                  className={cn(
                    "flex cursor-default select-none items-center rounded-sm px-2 text-sm outline-none",
                    "hover:bg-primary/15 focus:bg-primary/15",
                    isSelected && "bg-primary/10 text-foreground",
                  )}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")}
                  />
                  {/* eslint-disable-next-line signal/no-unwrapped-clipped-text -- inside-an-open-overlay */}
                  <span className="truncate">{keyword}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
